import { useState, useEffect } from "react";
import { Transaction, FinancialStats } from "../types";

export function useTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>(() => {
    // Load from localStorage on initial render
    const saved = localStorage.getItem("stanza-transactions");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure we have an array with proper structure
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Error loading transactions from localStorage:", e);
        return [];
      }
    }
    return [];
  });

  // Save to localStorage whenever transactions change
  useEffect(() => {
    try {
      localStorage.setItem("stanza-transactions", JSON.stringify(transactions));
    } catch (e) {
      console.error("Error saving transactions to localStorage:", e);
    }
  }, [transactions]);

  const addTransaction = (transaction: Omit<Transaction, "id">) => {
    const newTransaction: Transaction = {
      ...transaction,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    };
    setTransactions((prev) => [newTransaction, ...prev]);
    return newTransaction;
  };

  const updateTransaction = (id: string, updates: Partial<Transaction>) => {
    setTransactions((prev) =>
      prev.map((transaction) =>
        transaction.id === id ? { ...transaction, ...updates } : transaction
      )
    );
  };

  const deleteTransaction = (id: string) => {
    setTransactions((prev) =>
      prev.filter((transaction) => transaction.id !== id)
    );
  };

  const getTransaction = (id: string) => {
    return transactions.find((transaction) => transaction.id === id);
  };

  const importTransactions = (transactionsData: Omit<Transaction, "id">[]) => {
    const newTransactions: Transaction[] = transactionsData.map(
      (transaction) => ({
        ...transaction,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      })
    );

    setTransactions((prev) => [...newTransactions, ...prev]);
    return newTransactions;
  };

  // Calculate financial stats from transactions
  const calculateStats = (): FinancialStats => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const monthlyTransactions = transactions.filter((t) => {
      try {
        const date = new Date(t.date);
        return (
          date.getMonth() === currentMonth && date.getFullYear() === currentYear
        );
      } catch (e) {
        return false;
      }
    });

    const monthlyIncome = monthlyTransactions
      .filter((t) => t.type === "income")
      .reduce((sum, t) => sum + t.amount, 0);

    const monthlyExpenses = monthlyTransactions
      .filter((t) => t.type === "expense")
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    const totalIncome = transactions
      .filter((t) => t.type === "income")
      .reduce((sum, t) => sum + t.amount, 0);

    const totalExpenses = transactions
      .filter((t) => t.type === "expense")
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    const totalBalance = totalIncome - totalExpenses;

    const savingsRate =
      monthlyIncome > 0
        ? ((monthlyIncome - monthlyExpenses) / monthlyIncome) * 100
        : 0;

    return {
      totalBalance,
      monthlyIncome,
      monthlyExpenses,
      savingsRate: Number(savingsRate.toFixed(1)),
    };
  };

  return {
    transactions,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    getTransaction,
    importTransactions,
    stats: calculateStats(),
  };
}
